import { ApolloQueryResult } from '@apollo/client';
import { useCallback } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Button, DrawerBody, Subhead, cn } from '@growtherapy/sprout-ui';

import { getLoginOptions } from '../../auth';
import { DrawerHeader } from '../../components/DrawerHeader';
import { useTheme } from '../../hooks';
import { GetCurrentIntentionQuery } from '../../hooks/intentions/useGetCurrentIntentionQuery';
import { useWindowFocusEffect } from '../../hooks/useWindowFocusEffect';
import { Theme } from '../../state';
import { useClientLogin } from '../introduction-room/auth/useClientLogin';

function useRefetchOnRefocus(refetchAndValidate: () => Promise<ApolloQueryResult<GetCurrentIntentionQuery>>) {
  const refetchOnRefocus = useCallback(
    async (isFocused: boolean) => {
      if (!isFocused) return;
      await refetchAndValidate();
    },
    [refetchAndValidate],
  );

  useWindowFocusEffect(refetchOnRefocus);
}

export function UnauthenticatedIntentionsDrawer({
  onRefetch,
}: {
  onRefetch: () => Promise<ApolloQueryResult<GetCurrentIntentionQuery>>;
}) {
  const t = useTranslate();
  const { theme } = useTheme();
  const isDarkTheme = theme === Theme.DARK;
  const { handleLogin } = useClientLogin();
  const onPressReLogin = useCallback(() => {
    handleLogin(getLoginOptions(true));
  }, [handleLogin]);

  useRefetchOnRefocus(onRefetch);

  return (
    <>
      <DrawerHeader>
        <div className='flex items-center'>
          <Subhead.Small className={cn({ 'rebrand:text-text-inverse': isDarkTheme })}>
            {t('telehealth-core:intentions.intention')}
          </Subhead.Small>
        </div>
      </DrawerHeader>
      <DrawerBody className='flex flex-col my-auto text-center gap-400 grow-0 items-center mx-600'>
        <Subhead.Small className={cn({ 'rebrand:text-text-inverse': isDarkTheme })}>
          {t('telehealth-core:intentions.logInToAccessYourIntention')}
        </Subhead.Small>
        <Button onClick={onPressReLogin} className='w-full' use='secondary'>
          {t('telehealth-core:core.logIn')}
        </Button>
      </DrawerBody>
    </>
  );
}
